'use client';

import { useTranslations } from 'next-intl';
import { useFormatPrice } from 'hooks/pricing/useFormattedPrice';
import {
  MAX_PLAN_KEYS,
  MaxPlanKey,
  MaxStoreConfig,
} from 'content/products/max';
import { useMaxPlanCopy } from './hooks/useMaxPlanCopy';
import styles from './MaxPlanPicker.module.css';

interface MaxPlanPickerProps {
  config: MaxStoreConfig;
  currencySymbol: string;
  value: MaxPlanKey;
  onChange: (plan: MaxPlanKey) => void;
  /** Prefix for GTM `data-buttontype`, e.g. "cart-addon" → "cart-addon-select-max-yearly". */
  analyticsPrefix: string;
  disabled?: boolean;
  className?: string;
}

/** Plan radio group (Yearly / Monthly, or "3 months" on IN) shared by the cart rail, upsell modal and /shop. */
export const MaxPlanPicker = ({
  config,
  currencySymbol,
  value,
  onChange,
  analyticsPrefix,
  disabled = false,
  className,
}: MaxPlanPickerProps) => {
  const t = useTranslations('cart');
  const formatPrice = useFormatPrice();
  const { label, suffix, yearlySaving } = useMaxPlanCopy();
  const saveBadge = yearlySaving(config, currencySymbol);

  return (
    <div
      className={className ? `${styles.root} ${className}` : styles.root}
      role="radiogroup"
      aria-label={t('sharedCart.max.chooseSubscription')}
    >
      {MAX_PLAN_KEYS.map((plan) => {
        const selected = value === plan;
        return (
          <button
            key={plan}
            type="button"
            role="radio"
            aria-checked={selected}
            className={`${styles.option} ${selected ? styles.selected : ''} gtm`}
            onClick={() => onChange(plan)}
            disabled={disabled}
            data-buttontype={`${analyticsPrefix}-select-max-${plan}`}
            data-testid={`max-plan-${plan}`}
          >
            <span className={styles.label}>{label(config, plan)}</span>
            <span className={styles.price}>
              {currencySymbol}
              {formatPrice(config.plans[plan].price)}
              {suffix(config, plan)}
            </span>
            {plan === 'yearly' && saveBadge ? (
              <span className={styles.saveBadge}>{saveBadge}</span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
};
